import { config, isCookieAuthProxiedByDevServer } from '../config/config';

/**
 * Path prefix the Vite dev server proxies to the API gateway (see `server.proxy` in
 * `vite.config.ts`). Relative on purpose — that is the whole point.
 */
const DEV_PROXY_API_PREFIX: string = '/api';

const getGatewayBase = (): string => config.apiGateway.replace(/\/+$/, '');

/**
 * Base for the two endpoints that authenticate with the engine's session **cookie** rather than
 * a bearer token: the Better Auth engine itself and the token bridge.
 *
 * Mobile is served from a different origin than the gateway in every environment — `:8080` next
 * to the gateway's `:5000` locally, `m.dash.trusstor.com` next to `dash.trusstor.com` deployed —
 * so unlike the desktop app it is never same-origin with the engine. A credentialed
 * cross-origin request needs `Access-Control-Allow-Credentials: true` alongside an exact-origin
 * `Access-Control-Allow-Origin`, and the gateway sends neither (`cors.lua` is invoked as
 * `lua.cors "*" "*" "*"`, and `*` is not a value a credentialed request may accept).
 *
 * `vite dev` therefore routes these two calls through the dev server's own proxy, which makes
 * them same-origin from the browser's point of view and sidesteps CORS entirely — the same trick
 * the desktop app uses in dev.
 *
 * `isCookieAuthProxiedByDevServer` is decided in `config/config.ts`, next to (and as part of)
 * the same `isCookieAuthProxiedByDevServer` the config derives, so the base URL and the
 * makes engine calls work cannot drift apart: a build with no proxy and no same-origin gateway
 * has the flag off and never reaches this function.
 *
 * DEPLOYED ENVIRONMENTS ARE NOT SOLVED BY THIS. Serving mobile's engine login from a deployed
 * environment requires HAProxy to echo the mobile origin and allow credentials first; that is a
 * gateway + Auth CORS change, deliberately out of scope here.
 */
const getCookieAuthBase = (): string => {
	if (isCookieAuthProxiedByDevServer) {
		// Absolute on purpose: Better Auth's `createAuthClient` runs `new URL(baseURL)` and
		// throws on a protocol-less relative path. A same-origin ABSOLUTE URL routes through
		// the dev server's proxy exactly like a relative one.
		return `${window.location.origin}${DEV_PROXY_API_PREFIX}`;
	}
	return getGatewayBase();
};

/** Better Auth engine mount — matches the Auth service's `basePath: '/api/auth/engine'`. */
export const getAuthEngineBaseUrl = (): string => `${getCookieAuthBase()}/auth/engine`;

/** The bridge: engine session in, legacy `ILoggedUser` out. */
export const getAuthBridgeUrl = (): string => `${getCookieAuthBase()}/auth/engine-session/tokens`;
